import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api.js';
import Spinner from '../components/Spinner.jsx';
import Markdown from '../components/Markdown.jsx';

const CHAPTERS = [
  { start: 1,  end: 12, title: 'Physikalische Grundgesetze' },
  { start: 13, end: 22, title: 'Auftrieb & physikalische Effekte' },
  { start: 23, end: 28, title: 'Nullzeit & Dekompression' },
  { start: 29, end: 36, title: 'Gasplanung' },
  { start: 37, end: 53, title: 'Tauchkrankheiten & Notfälle' },
];

function chapterOf(order) {
  return CHAPTERS.findIndex((c) => order >= c.start && order <= c.end) + 1;
}

// ─── Aufgaben-Schritt ─────────────────────────────────────────────────────────
function TaskStep({ step, onComplete }) {
  const [chosen, setChosen]   = useState(null);
  const [result, setResult]   = useState(null);
  const [submitting, setSub]  = useState(false);
  const [openAnswer, setOpen] = useState('');

  const hasQuestion = !!step.question_id;
  const qType       = step.q_type;
  const qOptions    = step.q_options;

  const toggle = (i) => {
    if (result) return;
    if (qType === 'single' || qType === 'truefalse') setChosen([i]);
    else if (qType === 'multi') {
      setChosen((prev) => {
        const arr = prev ?? [];
        return arr.includes(i) ? arr.filter((x) => x !== i) : [...arr, i];
      });
    }
  };

  const submit = async () => {
    if (submitting) return;
    setSub(true);
    if (hasQuestion) {
      const payload = qType === 'cloze' ? openAnswer : chosen ?? [];
      try {
        const r = await api.submitAttempt({ question_id: step.question_id, chosen: payload });
        setResult(r);
        if (!step.gate || r.correct) setTimeout(onComplete, 1400);
      } finally { setSub(false); }
    } else {
      setSub(false);
      setResult({ correct: true, explanation: '' });
      setTimeout(onComplete, 700);
    }
  };

  const isSelected = (i) => Array.isArray(chosen) && chosen.includes(i);

  // Nach korrekter Antwort / nicht-gate-Schritt
  if (result && (result.correct || !step.gate)) {
    return (
      <div className={`rounded-xl p-4 text-sm border ${result.correct ? 'bg-teal-50 border-teal-200 text-teal-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
        <p className="font-semibold mb-1">{result.correct ? '🫧 Richtig!' : "⚠️ Nicht ganz — aber weiter geht's."}</p>
        {result.explanation && <p className="leading-relaxed">{result.explanation}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Step-eigener Prompt */}
      {step.prompt && (
        <div className="rounded-xl bg-ocean-50 border border-ocean-200 p-4 text-sm whitespace-pre-line">
          {step.prompt}
        </div>
      )}

      {/* Frage-Prompt aus der questions-Tabelle */}
      {hasQuestion && step.q_prompt && (
        <p className="font-medium text-gray-900">{step.q_prompt}</p>
      )}

      {/* Antwort-Optionen */}
      {hasQuestion && qOptions && (qType === 'single' || qType === 'multi' || qType === 'truefalse') && (
        <div className="space-y-2">
          {qOptions.map((opt, i) => {
            const sel  = isSelected(i);
            const corr = result?.answer?.includes(i);
            const wrong = result && sel && !corr;
            return (
              <button key={i} onClick={() => toggle(i)}
                className={`w-full text-left rounded-xl border px-4 py-3 text-sm transition-all ${
                  wrong  ? 'bg-red-50 border-red-400 text-red-800' :
                  corr   ? 'bg-teal-50 border-teal-400 text-teal-800' :
                  sel    ? 'bg-ocean-50 border-ocean-400 text-ocean-800' :
                  result ? 'bg-gray-50 border-gray-200 text-gray-400' :
                           'bg-white border-gray-200 hover:border-ocean-300 hover:bg-ocean-50 text-gray-800'
                }`}>
                <span className="font-mono font-semibold mr-2 text-ocean-400">{String.fromCharCode(65 + i)}.</span>{opt}
              </button>
            );
          })}
        </div>
      )}

      {/* Lückentext (inkl. Format-Hinweis) */}
      {hasQuestion && qType === 'cloze' && (
        <div className="space-y-1.5">
          <input className="input" placeholder="Antwort eingeben…"
            value={openAnswer} onChange={(e) => setOpen(e.target.value)}
            disabled={!!result} />
          <p className="text-xs text-gray-400">
            💡 Tipp: Gib nur den gesuchten Begriff oder die Zahl ein — ohne Einheit, z. B. <span className="font-mono">2,5</span> oder <span className="font-mono">verdrängten</span>.
          </p>
        </div>
      )}

      {/* Fehler-Feedback (gate-Aufgaben) */}
      {result && !result.correct && step.gate && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm">
          <p className="font-semibold text-red-700">❌ Falsch — bitte nochmal versuchen.</p>
          {result.explanation && <p className="mt-1 text-red-600">{result.explanation}</p>}
        </div>
      )}

      {/* Submit-Button */}
      {!result && (
        <button onClick={submit}
          disabled={submitting || (hasQuestion && qType !== 'cloze' && (!chosen || chosen.length === 0))}
          className="btn-primary w-full">
          {submitting ? 'Prüfen…' : hasQuestion ? 'Antwort prüfen' : 'Verstanden → Weiter'}
        </button>
      )}
    </div>
  );
}

// ─── Inhaltsverzeichnis ───────────────────────────────────────────────────────
function TableOfContents({ steps, currentIdx, onJump }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)}
        className="btn-secondary text-xs flex items-center gap-2">
        ☰ Inhaltsverzeichnis
        <span className="text-gray-400">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-white rounded-xl shadow-lg border border-ocean-200 w-64 sm:w-72 max-h-80 overflow-y-auto">
          {CHAPTERS.map((c, ci) => {
            const chSteps   = steps.filter((s) => s.ord >= c.start && s.ord <= c.end);
            const chDone    = chSteps.filter((s) => s.completed).length;
            const firstIdx  = steps.findIndex((s) => s.ord >= c.start && s.ord <= c.end);
            const active    = chapterOf(steps[currentIdx]?.ord) === ci + 1;

            return (
              <button key={ci}
                onClick={() => { onJump(firstIdx); setOpen(false); }}
                className={`w-full text-left px-4 py-3 text-sm border-b border-gray-100 last:border-0 hover:bg-ocean-50 transition-colors ${active ? 'bg-ocean-50 font-semibold text-ocean-700' : 'text-gray-700'}`}>
                <div className="flex items-center justify-between">
                  <span>Kap. {ci + 1}: {c.title}</span>
                  <span className={`text-xs font-mono ml-2 shrink-0 ${chDone === chSteps.length ? 'text-teal-500' : 'text-gray-400'}`}>
                    {chDone}/{chSteps.length}
                  </span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full bg-ocean-400 transition-all"
                    style={{ width: `${chSteps.length > 0 ? (chDone / chSteps.length) * 100 : 0}%` }} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function LernreiheView() {
  const { id } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [currentIdx, setIdx]  = useState(0);

  useEffect(() => {
    setLoading(true); setError(null);
    api.getLernreihe(id)
      .then((d) => {
        setData(d);
        const firstOpen = d.steps.findIndex((s) => !s.completed);
        setIdx(firstOpen >= 0 ? firstOpen : d.steps.length - 1);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const completeStep = useCallback(async (stepId) => {
    await api.completeStep(stepId);
    setData((prev) => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        steps: prev.steps.map((s) => s.id === stepId ? { ...s, completed: true } : s),
      };
      setIdx((i) => Math.min(i + 1, updated.steps.length - 1));
      return updated;
    });
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  if (error)   return <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">Fehler: {error}</div>;
  if (!data)   return <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">Lernreihe nicht gefunden.</div>;

  const steps   = data.steps;
  const step    = steps[currentIdx];
  const done    = steps.filter((s) => s.completed).length;
  const chapter = step ? chapterOf(step.ord) : 0;
  const pct     = steps.length > 0 ? Math.round((done / steps.length) * 100) : 0;

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Nav */}
      <div className="flex items-center gap-3">
        <Link to="/lernen" className="text-sm text-ocean-600 hover:text-ocean-800 transition-colors">← Zurück</Link>
        <span className="font-bold text-gray-800 flex-1 text-center text-sm">{data.title}</span>
        <TableOfContents steps={steps} currentIdx={currentIdx} onJump={setIdx} />
      </div>

      {/* Fortschritt */}
      <div className="space-y-1.5">
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">{step ? `Kapitel ${chapter}: ${CHAPTERS[chapter - 1]?.title}` : ''}</span>
          <span className="font-mono dive-value">{done}/{steps.length} · {pct} %</span>
        </div>
        <div className="h-2 rounded-full bg-ocean-100 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #0ea5e9, #06b6d4)' }} />
        </div>
        {/* Kapitel-Chips */}
        <div className="flex gap-1 flex-wrap pt-0.5">
          {CHAPTERS.map((c, i) => {
            const chDone  = steps.filter((s) => s.ord >= c.start && s.ord <= c.end && s.completed).length;
            const chTotal = steps.filter((s) => s.ord >= c.start && s.ord <= c.end).length;
            const active  = chapter === i + 1;
            const firstIdx = steps.findIndex((s) => s.ord >= c.start && s.ord <= c.end);
            return (
              <button key={i} title={c.title} onClick={() => setIdx(firstIdx)}
                className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                  active              ? 'bg-ocean-700 text-white' :
                  chDone === chTotal  ? 'bg-teal-100 text-teal-700 hover:bg-teal-200' :
                                        'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}>
                {i + 1}
              </button>
            );
          })}
        </div>
      </div>

      {/* Schritt-Karte */}
      {step && (
        <div className="card border-ocean-200 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-ocean-500">
                {step.type === 'task' ? '📝 Aufgabe' : '📖 Inhalt'}
                {step.type === 'task' && step.gate && <span className="ml-1 text-coral-500">· Pflicht</span>}
              </p>
              {step.title && <h2 className="font-bold text-gray-900 mt-0.5">{step.title}</h2>}
            </div>
            {step.completed && <span className="badge bg-teal-100 text-teal-700 shrink-0">✓ erledigt</span>}
          </div>

          {/* Inhaltsschritt */}
          {step.type === 'content' && step.body_md && (
            <Markdown text={step.body_md}
              className="prose prose-sm max-w-none text-gray-700 space-y-2" />
          )}

          {/* Bilder */}
          {step.type === 'content' && Array.isArray(step.media) && step.media.length > 0 && (
            <div className="space-y-2">
              {step.media.map((m, i) => (
                <img key={i} src={`/lernreihe/${m}`} alt={step.title ?? ''}
                  className="rounded-xl max-w-full border border-ocean-100 shadow-sm" loading="lazy" />
              ))}
            </div>
          )}

          {/* Weiter-Button (Inhaltsschritte, noch nicht abgeschlossen) */}
          {step.type === 'content' && !step.completed && (
            <button onClick={() => completeStep(step.id)} className="btn-primary w-full">
              Weiter →
            </button>
          )}

          {/* Aufgabe */}
          {step.type === 'task' && (
            <TaskStep step={step} onComplete={() => completeStep(step.id)} />
          )}
        </div>
      )}

      {/* Abgeschlossen */}
      {done === steps.length && (
        <div className="card text-center border-teal-200 bg-teal-50">
          <p className="text-3xl mb-2">🎉</p>
          <p className="font-bold text-teal-800">Lernreihe abgeschlossen!</p>
          <Link to="/lernen/quiz" className="btn-primary mt-3 inline-flex">
            Zum Quiz-Modus →
          </Link>
        </div>
      )}

      {/* Navigation unten */}
      <div className="flex items-center justify-between pt-1">
        <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={currentIdx === 0}
          className="btn-secondary text-xs disabled:opacity-40">← Zurück</button>
        <span className="text-xs font-mono text-gray-400">Schritt {currentIdx + 1}/{steps.length}</span>
        <button onClick={() => setIdx((i) => Math.min(steps.length - 1, i + 1))} disabled={currentIdx === steps.length - 1}
          className="btn-secondary text-xs disabled:opacity-40">Weiter →</button>
      </div>
    </div>
  );
}
